import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { Shift } from 'server/shared/models/shift';
import { EditTimeDialog } from '../job-edit/dialogs/edit-time-dialog/edit-time-dialog.component';

@Component({
  selector: 'app-job-shifts',
  templateUrl: './job-shifts.component.html',
  styleUrls: ['./job-shifts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobShiftsComponent {
  shifts$ = new BehaviorSubject<Shift[]>([]);
  @Input() set shifts(value: Shift[]) {
    this.shifts$.next(value);
  }
  get shifts() {
    return this.shifts$.value;
  }
  availableShifts$ = new BehaviorSubject<Shift[]>([]);
  @Input() set availableShifts(value: Shift[]) {
    this.availableShifts$.next(value);
  }
  get availableShifts() {
    return this.availableShifts$.value;
  }
  isNewJob$ = new BehaviorSubject<boolean>(true);
  @Input() set isNewJob(value: boolean) {
    this.isNewJob$.next(value);
  }
  get isNewJob() {
    return this.isNewJob$.value;
  }
  hasMultipleShifts$ = new BehaviorSubject<boolean>(false);
  @Input() set hasMultipleShifts(value: boolean) {
    this.hasMultipleShifts$.next(value);
  }
  get hasMultipleShifts() {
    return this.hasMultipleShifts$.value;
  }
  canEditShifts$ = new BehaviorSubject<boolean>(false);
  @Input() set canEditShifts(value: boolean) {
    this.canEditShifts$.next(value);
  }
  get canEditShifts() {
    return this.canEditShifts$.value;
  }
  @Input() hasAvailableOrAcceptedInvitees: boolean = false;
  invalid$ = new BehaviorSubject<boolean>(false);
  @Input() set invalid(value: boolean) {
    this.invalid$.next(value);
  }
  get invalid() {
    return this.invalid$.value;
  }

  @Output() shiftsChange = new EventEmitter<Shift[]>();
  @Output() hasMultipleShiftsChange = new EventEmitter<boolean>();
  @Output() shiftSelected = new EventEmitter<void>();

  constructor(private dialog: MatDialog) {}

  trackByShift(index: number, shift: Shift): string {
    return shift.id || index.toString();
  }

  toggleHasMultipleShifts(event: any) {
    this.hasMultipleShiftsChange.emit(event.checked);
  }

  selectShift() {
    this.shiftSelected.emit();
  }

  isShiftSelected(shift: Shift): boolean {
    return this.shifts$.value.some((s) => s.id === shift.id);
  }

  private sortShifts(shifts: Shift[]): Shift[] {
    return [...shifts].sort((a, b) => {
      // First sort by shiftEnum
      if (a.shift !== b.shift) {
        return a.shift - b.shift;
      }
      // Then sort by time (from)
      return a.from - b.from;
    });
  }

  toggleShift(event: MatCheckboxChange, shift: Shift) {
    console.log({
      event,
      shift,
    });

    if (event.checked) {
      const matchingShift = this.availableShifts$.value.find(
        (s) => s.id === shift.id,
      );
      const updatedShifts = this.sortShifts([
        ...this.shifts$.value,
        matchingShift || shift,
      ]);
      this.shifts$.next(updatedShifts);
    } else {
      const updatedShifts = this.sortShifts(
        this.shifts$.value.filter((s) => s.id !== shift.id),
      );
      this.shifts$.next(updatedShifts);
    }
    this.shiftsChange.emit(this.shifts$.value);
    this.selectShift();
  }

  ngModelSetFirstShift(shift: Shift) {
    const matchingShift = this.availableShifts$.value.find(
      (s) => s.id === shift.id,
    );
    this.shifts$.next([matchingShift || shift]);
    this.shiftsChange.emit(this.shifts$.value);
    this.selectShift();
  }

  getSelectedShiftFromAvailable(): Shift | undefined {
    const selectedShift = this.shifts$.value[0];
    if (!selectedShift) return undefined;
    return this.availableShifts$.value.find((s) => s.id === selectedShift.id);
  }

  openShiftEditDialog(event: Event, shift: Shift): void {
    event.preventDefault();
    event.stopPropagation();

    const dialogRef = this.dialog.open(EditTimeDialog, {
      data: shift,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Update the shift times in place
        shift.from = result.from;
        shift.to = result.to;

        // Update availableShifts to trigger change detection and make changes visible immediately
        const updatedAvailableShifts = this.availableShifts$.value.map((s) => {
          if (s.id === shift.id) {
            // Create a new object with updated times
            return { ...s, from: result.from, to: result.to };
          }
          return s;
        });
        this.availableShifts$.next(updatedAvailableShifts);

        // If this shift is currently selected, also update the selected shifts array
        const selectedIndex = this.shifts$.value.findIndex(
          (s) => s.id === shift.id,
        );
        if (selectedIndex !== -1) {
          // Create new array with updated shift to trigger change detection
          const updatedShifts = this.shifts$.value.map((s) => {
            if (s.id === shift.id) {
              // Create a new object with updated times
              return { ...s, from: result.from, to: result.to };
            }
            return s;
          });

          // Sort shifts after updating times
          const sortedShifts = this.sortShifts(updatedShifts);

          // Update local state and emit to parent
          this.shifts$.next(sortedShifts);
          this.shiftsChange.emit(sortedShifts);
        }
      }
    });
  }
}
